import dragon1 from "../assets/images/avatars/dragon_1.png";
import dragon2 from "../assets/images/avatars/dragon_2.png";
// Avatar utility functions
import person1 from "../assets/images/avatars/person_1.png";
import person2 from "../assets/images/avatars/person_2.png";
import person3 from "../assets/images/avatars/person_3.png";

// Available avatar images
export const avatarImages = [person1, person2, person3, dragon1, dragon2];

const SESSION_AVATAR_KEY = "userSessionAvatar";

let ephemeralAvatar = null;

const safeSessionGet = key => {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
};

const safeSessionSet = (key, value) => {
  try {
    sessionStorage.setItem(key, value);
    return true;
  } catch {
    return false;
  }
};

const safeSessionRemove = key => {
  try {
    sessionStorage.removeItem(key);
  } catch {
    // storage unavailable
  }
};

const getDeterministicAvatarIndex = userData => {
  const seed = userData?.sub || userData?.email || userData?.preferred_username || "";
  if (!seed) {
    return Math.floor(Math.random() * avatarImages.length);
  }

  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    hash = (hash + seed.charCodeAt(i)) % avatarImages.length;
  }
  return hash;
};

const pickAvatarWithoutStorage = userData => {
  if (userData) {
    return avatarImages[getDeterministicAvatarIndex(userData)];
  }

  if (!ephemeralAvatar) {
    ephemeralAvatar = avatarImages[Math.floor(Math.random() * avatarImages.length)];
  }
  return ephemeralAvatar;
};

/**
 * Get saved avatar URL from localStorage (set by AvatarEditor)
 * @returns {string|null}
 */
const getSavedAvatarUrl = () => {
  try {
    const raw = localStorage.getItem("ayg_avatar");
    if (raw) {
      const parsed = JSON.parse(raw);
      return parsed.url ?? null;
    }
  } catch {
    // ignore
  }
  return null;
};

/**
 * Get a user's avatar image based on their data
 * @param {Object} userData - User data object
 * @param {string} userData.avatar - User's selected avatar (if any)
 * @returns {string} Avatar image URL
 */
export const getUserAvatarImage = userData => {
  // Check for custom avatar saved via AvatarEditor
  const savedUrl = getSavedAvatarUrl();
  if (savedUrl) return savedUrl;

  // If user has a specific avatar selected, use it
  if (userData?.avatar && avatarImages.includes(userData.avatar)) {
    return userData.avatar;
  }

  // Check if we have a session avatar stored
  const sessionAvatar = safeSessionGet(SESSION_AVATAR_KEY);
  if (sessionAvatar && avatarImages.includes(sessionAvatar)) {
    return sessionAvatar;
  }

  // Otherwise, select an avatar for this session and store it when possible
  const selectedAvatar = pickAvatarWithoutStorage(userData);
  safeSessionSet(SESSION_AVATAR_KEY, selectedAvatar);

  return selectedAvatar;
};

/**
 * Get user initials as fallback
 * @param {Object} userData - User data object
 * @returns {string} User initials
 */
export const getUserInitials = userData => {
  if (!userData) {
    return "";
  }

  const firstName = userData.given_name || userData.firstName || "";
  const lastName = userData.family_name || userData.lastName || "";

  return (firstName[0] || "") + (lastName[0] || "");
};

/**
 * Clear session avatar (call on logout)
 */
export const clearSessionAvatar = () => {
  safeSessionRemove(SESSION_AVATAR_KEY);
  ephemeralAvatar = null;
};
